import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, ChevronRight, Loader2, Save, Search } from "lucide-react";
import { fetchPropertyConfig, savePropertyConfig } from "@/lib/api";

function groupByGroup(props, groupLabels = {}) {
  const groups = {};
  for (const p of props) {
    const key = p.group_name || "other";
    if (!groups[key]) groups[key] = { label: groupLabels[key] || formatGroupLabel(key), props: [] };
    groups[key].props.push(p);
  }
  return Object.entries(groups)
    .sort(([, a], [, b]) => a.label.localeCompare(b.label))
    .map(([key, { label, props }]) => [key, label, props]);
}

function formatGroupLabel(raw) {
  return raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function PropGroup({ groupKey, label, props, selected, onToggle, searchQuery }) {
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!searchQuery) return props;
    const q = searchQuery.toLowerCase();
    return props.filter((p) => p.label.toLowerCase().includes(q) || p.name.toLowerCase().includes(q));
  }, [props, searchQuery]);

  if (filtered.length === 0) return null;

  const checkedCount = filtered.filter((p) => selected.has(p.name)).length;

  // Auto-expand groups with matches when searching
  const isOpen = searchQuery ? true : open;

  return (
    <div className="border border-[#dddddd] rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => !searchQuery && setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 bg-[#f5f5f5] text-left text-sm font-semibold text-[#323c44] hover:bg-[#efefef] transition"
      >
        <span>{formatGroupLabel(label)}</span>
        <div className="flex items-center gap-3">
          {checkedCount > 0 && (
            <span className="rounded-full bg-[#50e3c2]/10 px-2 py-0.5 text-xs font-semibold text-[#119996]">
              {checkedCount} selected
            </span>
          )}
          {!searchQuery && (isOpen ? <ChevronDown className="h-4 w-4 text-[#7d8b96]" /> : <ChevronRight className="h-4 w-4 text-[#7d8b96]" />)}
        </div>
      </button>
      {isOpen && (
        <div className="divide-y divide-[#efefef]">
          {filtered.map((p) => (
            <div
              key={p.name}
              role="checkbox"
              aria-checked={selected.has(p.name)}
              tabIndex={0}
              onClick={() => onToggle(p.name)}
              onKeyDown={(e) => (e.key === " " || e.key === "Enter") && onToggle(p.name)}
              className="flex cursor-pointer items-start gap-3 px-4 py-2.5 hover:bg-[#f5f5f5] transition select-none"
            >
              <div className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition ${selected.has(p.name) ? "border-[#57c1a9] bg-[#50e3c2]" : "border-[#cccccc] bg-white"}`}>
                {selected.has(p.name) && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#323c44]">{p.label}</p>
                <p className="text-xs text-[#7d8b96]">{p.name} · {p.type}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PropertyConfigPanel({ portalId, onSaved }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [searchDeal, setSearchDeal] = useState("");
  const [searchLi, setSearchLi] = useState("");
  const [selectedDeal, setSelectedDeal] = useState(new Set());
  const [selectedLi, setSelectedLi] = useState(new Set());
  const [activeTab, setActiveTab] = useState("deal");

  useEffect(() => {
    if (!portalId) return;
    setLoading(true);
    fetchPropertyConfig(portalId)
      .then((data) => {
        setConfig(data);
        setSelectedDeal(new Set(data.selected_deal_props || []));
        setSelectedLi(new Set(data.selected_li_props || []));
      })
      .catch((e) => setError(e.response?.data?.detail || "Failed to load properties."))
      .finally(() => setLoading(false));
  }, [portalId]);

  const toggleDeal = (name) => setSelectedDeal((prev) => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  const toggleLi = (name) => setSelectedLi((prev) => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess(false);
    try {
      await savePropertyConfig(portalId, [...selectedDeal], [...selectedLi]);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const dealGroups = useMemo(() => groupByGroup(config?.deal_properties || [], config?.deal_group_labels || {}), [config]);
  const liGroups = useMemo(() => groupByGroup(config?.li_properties || [], config?.li_group_labels || {}), [config]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-[#4a5b68]">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading properties from HubSpot…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-[#4a5b68]">
            Choose which deal and line item properties are pulled from HubSpot during a sync.
            Only selected properties will appear as filters in reporting.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex flex-shrink-0 items-center gap-2 rounded-lg bg-[#50e3c2] px-4 py-2 text-sm font-semibold text-[#323c44] shadow-sm hover:bg-[#50e3c2] disabled:opacity-60 transition"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save & re-sync
        </button>
      </div>

      {error && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">{error}</p>}
      {success && <p className="rounded-lg bg-emerald-50 px-4 py-2 text-sm text-emerald-700">✓ Saved — next sync will use these properties.</p>}

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg border border-[#dddddd] bg-[#f5f5f5] p-1 w-fit">
        {[
          { id: "deal", label: `Deal properties`, count: selectedDeal.size },
          { id: "li", label: `Line item properties`, count: selectedLi.size },
        ].map(({ id, label, count }) => (
          <button
            key={id}
            type="button"
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition ${activeTab === id ? "bg-white text-[#323c44] shadow-sm" : "text-[#4a5b68] hover:text-[#323c44]"}`}
          >
            {label}
            {count > 0 && (
              <span className="rounded-full bg-[#50e3c2] px-1.5 py-0.5 text-[10px] font-bold text-[#323c44] leading-none">
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7d8b96]" />
        <input
          type="text"
          placeholder={`Search ${activeTab === "deal" ? "deal" : "line item"} properties…`}
          value={activeTab === "deal" ? searchDeal : searchLi}
          onChange={(e) => activeTab === "deal" ? setSearchDeal(e.target.value) : setSearchLi(e.target.value)}
          className="w-full rounded-lg border border-[#dddddd] bg-white py-2 pl-9 pr-4 text-sm text-[#323c44] placeholder:text-[#7d8b96] focus:border-[#57c1a9] focus:outline-none"
        />
      </div>

      {/* Property groups */}
      <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
        {activeTab === "deal"
          ? dealGroups.map(([key, label, props]) => (
              <PropGroup
                key={key}
                groupKey={key}
                label={label}
                props={props}
                selected={selectedDeal}
                onToggle={toggleDeal}
                searchQuery={searchDeal}
              />
            ))
          : liGroups.map(([key, label, props]) => (
              <PropGroup
                key={key}
                groupKey={key}
                label={label}
                props={props}
                selected={selectedLi}
                onToggle={toggleLi}
                searchQuery={searchLi}
              />
            ))}
      </div>

      <p className="text-xs text-[#7d8b96]">
        {(activeTab === "deal" ? selectedDeal : selectedLi).size} selected ·{" "}
        {(activeTab === "deal" ? config?.deal_properties : config?.li_properties)?.length || 0} available
      </p>
    </div>
  );
}
